<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import WheelCanvas from './components/WheelCanvas.svelte';
  import ItemEditSheet from './components/ItemEditSheet.svelte';
  import ItemDetailSheet from './components/ItemDetailSheet.svelte';
  import SetManagerSheet from './components/SetManagerSheet.svelte';
  import SettingsSheet from './components/SettingsSheet.svelte';
  import HelpSheet from './components/HelpSheet.svelte';
  import HistorySheet from './components/HistorySheet.svelte';
  import AppSettingsSheet from './components/AppSettingsSheet.svelte';
  import SequentialRunSheet from './components/SequentialRunSheet.svelte';
  import type { SeqResult, SeqRunState } from './components/SequentialRunSheet.svelte';
  import RouletteManagerSheet from './components/RouletteManagerSheet.svelte';
  import GridView from './components/GridView.svelte';
  import CharacterEffect from './components/CharacterEffect.svelte';
  import MiniCharacterEffect from './components/MiniCharacterEffect.svelte';
  import {
    appState,
    initState,
    notifyChange,
    flushState,
    addSet,
    renameSet,
    deleteSet,
    setCurrentSet,
    replaceItems,
    updateItem,
    toggleItemEnabled,
    shuffleCurrentItems,
    restoreOrder,
    moveItem,
    setActiveRoulette,
    addRoulette,
    renameRoulette,
    deleteRoulette,
    duplicateRoulette,
    clearWinHistory,
    updatePreferences,
    setRouletteVisible,
    spinAll,
    spinRoulette,
    rt,
    dismissResult,
  } from './lib/state.svelte.ts';
  import type { RouletteId } from './lib/types';
  import { buildLayoutForSet, colorForItem, computeItemPercentages } from './lib/wheelLayout';
  import type { Item, Roulette, ItemSet, RouletteSettings, SetId } from './lib/types';
  import { soundManager } from './lib/soundManager';

  // ─────────────────────────────────────────────────────────
  //  状態
  // ─────────────────────────────────────────────────────────

  let ready = $state(false);

  // ビューモード: 'grid' = グリッド表示、'detail' = 単一ルーレット詳細
  let viewMode = $state<'grid' | 'detail'>('grid');

  // detail モード時のアクティブ runtime (= activeRouletteId のもの)
  const activeRt = $derived(rt(appState.activeRouletteId));
  // 既存コードとの互換のためエイリアス
  const wheelAngle = $derived(activeRt.wheelAngle);
  const spinPhase = $derived(activeRt.spinPhase);
  const winnerText = $derived(activeRt.winnerText);
  const winnerVisible = $derived(activeRt.winnerVisible);

  const visibleRoulettes = $derived(appState.roulettes.filter((r: Roulette) => r.visible));

  // シート開閉状態
  let itemEditOpen = $state(false);
  let setManagerOpen = $state(false);
  let settingsOpen = $state(false);
  let helpOpen = $state(false);
  let historyOpen = $state(false);
  let appSettingsOpen = $state(false);
  let rouletteManagerOpen = $state(false);
  let seqRunOpen = $state(false);
  let itemDetailIdx = $state<number | null>(null);

  // 並び替えモード
  let reorderMode = $state(false);

  // 連続実行
  let seqState = $state<SeqRunState>({ phase: 'idle' });
  let seqStopRequested = false;
  let seqEnabledSnapshot: boolean[] = [];

  // アクティブルーレット
  const roulette: Roulette = $derived(
    appState.roulettes.find((r: Roulette) => r.id === appState.activeRouletteId) ??
      appState.roulettes[0],
  );

  // 現在のセット
  const currentSet: ItemSet = $derived(
    roulette.sets.find((s: ItemSet) => s.id === roulette.currentSetId) ?? roulette.sets[0],
  );

  const isShuffled = $derived(
    appState.ui.shuffleOrder !== null &&
      appState.ui.shuffleOrder.setId === currentSet.id,
  );

  // displayLayout: shuffleOrder の mode に応じて適切な builder を呼ぶ
  const displayLayout = $derived(
    buildLayoutForSet(currentSet.items, currentSet.id, appState.ui.shuffleOrder),
  );

  // ─────────────────────────────────────────────────────────
  //  タップ / スペースキー処理 (detail モード)
  //  spin / sound / 履歴記録は state.svelte.ts の spinRoulette() で完結
  // ─────────────────────────────────────────────────────────

  function handleWheelTap(): void {
    switch (spinPhase) {
      case 'idle':
        spinRoulette(roulette.id);
        break;
      case 'spinning':
        // スピン中タップは無効
        break;
      case 'done':
        dismissResult(roulette.id);
        break;
    }
  }

  function handleKeydown(e: KeyboardEvent): void {
    if (e.code === 'Space') {
      e.preventDefault();
      handleWheelTap();
    }
  }

  // ─────────────────────────────────────────────────────────
  //  ライフサイクル
  // ─────────────────────────────────────────────────────────

  onMount(async () => {
    await initState();
    // 初期 view: visible が 1 件なら detail、複数なら grid
    const visible = appState.roulettes.filter((r: Roulette) => r.visible);
    viewMode = visible.length <= 1 ? 'detail' : 'grid';
    if (visible.length === 1 && appState.activeRouletteId !== visible[0].id) {
      setActiveRoulette(visible[0].id);
    }
    ready = true;
    window.addEventListener('keydown', handleKeydown);
    window.addEventListener('beforeunload', flushState);
  });

  // テーマモードを document.documentElement に反映
  $effect(() => {
    const mode = appState.preferences.themeMode;
    if (!mode || mode === 'auto') {
      document.documentElement.removeAttribute('data-theme');
    } else {
      document.documentElement.setAttribute('data-theme', mode);
    }
  });

  // visible が 1 件以下になったら自動的に detail に強制
  $effect(() => {
    if (!ready) return;
    if (visibleRoulettes.length <= 1 && viewMode !== 'detail') {
      if (visibleRoulettes.length === 1 && appState.activeRouletteId !== visibleRoulettes[0].id) {
        setActiveRoulette(visibleRoulettes[0].id);
      }
      viewMode = 'detail';
    }
  });

  onDestroy(() => {
    window.removeEventListener('keydown', handleKeydown);
    window.removeEventListener('beforeunload', flushState);
    activeRt.engine.reset();
  });

  // ─────────────────────────────────────────────────────────
  //  項目アクション
  // ─────────────────────────────────────────────────────────

  function toggleItem(itemIdx: number): void {
    toggleItemEnabled(currentSet.id, itemIdx);
  }

  function openItemDetail(itemIdx: number): void {
    if (spinPhase === 'spinning') return;
    itemDetailIdx = itemIdx;
  }

  function saveItemDetail(updated: Item): void {
    if (itemDetailIdx === null) return;
    updateItem(currentSet.id, itemDetailIdx, updated);
  }

  function openItemEdit(): void {
    if (spinPhase === 'spinning') return;
    itemEditOpen = true;
  }

  function saveItemEdit(items: Item[]): void {
    replaceItems(currentSet.id, items);
  }

  // ─────────────────────────────────────────────────────────
  //  セットアクション
  // ─────────────────────────────────────────────────────────

  function openSetManager(): void {
    if (spinPhase === 'spinning') return;
    setManagerOpen = true;
  }

  function handleSetSelect(id: SetId): void {
    setCurrentSet(id);
  }

  // ─────────────────────────────────────────────────────────
  //  設定アクション
  // ─────────────────────────────────────────────────────────

  function openSettings(): void {
    if (spinPhase === 'spinning') return;
    soundManager.init(import.meta.env.BASE_URL);
    settingsOpen = true;
  }

  function saveSettings(s: RouletteSettings): void {
    roulette.settings = s;
    notifyChange();
  }

  // ─────────────────────────────────────────────────────────
  //  マーカードラッグ (Phase 6)
  // ─────────────────────────────────────────────────────────

  function handleMarkerDrag(deg: number): void {
    if (spinPhase === 'spinning') return;
    roulette.settings.markerPosition = 'free';
    roulette.settings.markerAngleDeg = deg;
    notifyChange();
  }

  // ─────────────────────────────────────────────────────────
  //  ルーレット管理 (Phase 8)
  // ─────────────────────────────────────────────────────────

  function openRouletteManager(): void {
    if (spinPhase === 'spinning') return;
    rouletteManagerOpen = true;
  }

  function handleRouletteSelect(id: RouletteId): void {
    setActiveRoulette(id);
  }

  // ─────────────────────────────────────────────────────────
  //  連続実行 (Phase 7)
  // ─────────────────────────────────────────────────────────

  function openSeqRun(): void {
    if (spinPhase === 'spinning') return;
    if (seqState.phase === 'done') seqState = { phase: 'idle' };
    seqRunOpen = true;
  }

  function spinOnceAsync(): Promise<{ index: number; text: string }> {
    return new Promise((resolve) => {
      const s = roulette.settings;
      const layout = buildLayoutForSet(currentSet.items, currentSet.id, appState.ui.shuffleOrder);
      activeRt.engine.setOneShotComplete((idx, text) => {
        const origIdx = layout.segments[idx]?.itemIndex ?? -1;
        resolve({ index: origIdx, text });
      });
      if (!activeRt.engine.start(
        layout,
        s.markerPosition,
        s.markerAngleDeg,
        s.spinDurationMs,
        s.segmentDirection,
        s.spinDirection,
        {
          durationRandomRatio: s.spinDurationRandomRatio,
          phaseOverrides: s.spinPhaseOverrides,
          presetProfile: s.spinPresetProfile,
          phaseRandomize: s.spinPhaseRandomize,
          presetRandom: s.spinPresetRandom,
          durationRandom: s.spinDurationRandom,
        },
      )) {
        resolve({ index: -1, text: '' });
        return;
      }
      activeRt.spinPhase = 'spinning';
    });
  }

  function sleep(ms: number): Promise<void> {
    return new Promise((r) => setTimeout(r, ms));
  }

  async function startSequentialRun(count: number, holdSec: number): Promise<void> {
    if (seqState.phase === 'running') return;
    soundManager.init(import.meta.env.BASE_URL);
    soundManager.setVolume(appState.preferences.volume);

    seqEnabledSnapshot = currentSet.items.map((it: Item) => it.enabled);
    seqStopRequested = false;
    const results: SeqResult[] = [];
    seqState = { phase: 'running', current: 0, total: count, results: [...results] };
    // 実行中はシートを閉じてホイールを見せる
    seqRunOpen = false;

    for (let i = 0; i < count; i++) {
      if (seqStopRequested) break;
      // 候補がなくなったら中断
      if (currentSet.items.filter((it: Item) => it.enabled).length === 0) break;

      seqState = { phase: 'running', current: i + 1, total: count, results: [...results] };

      const winner = await spinOnceAsync();
      // 結果オーバーレイは非表示 (連続実行中は専用 UI で見せる)
      activeRt.winnerVisible = false;
      activeRt.engine.reset();
      activeRt.spinPhase = 'idle';

      if (winner.index < 0 || winner.text === '') break;
      results.push({ itemIndex: winner.index, text: winner.text });
      seqState = { phase: 'running', current: i + 1, total: count, results: [...results] };

      // 当選項目を無効化 (被りなし)
      if (winner.index >= 0 && winner.index < currentSet.items.length) {
        currentSet.items[winner.index].enabled = false;
        notifyChange();
      }

      if (i < count - 1 && !seqStopRequested) {
        await sleep(holdSec * 1000);
      }
    }

    // 復元
    for (let i = 0; i < seqEnabledSnapshot.length && i < currentSet.items.length; i++) {
      currentSet.items[i].enabled = seqEnabledSnapshot[i];
    }
    notifyChange();

    seqState = { phase: 'done', results };
    // 完了でシートを再オープンし結果を表示
    seqRunOpen = true;
  }

  function stopSequentialRun(): void {
    seqStopRequested = true;
  }

  // ─────────────────────────────────────────────────────────
  //  シャッフル / ランダム配置トグル
  // ─────────────────────────────────────────────────────────

  function shuffleNow(): void {
    if (spinPhase === 'spinning') return;
    shuffleCurrentItems(currentSet.id);
  }

  function toggleRandomEachSpin(): void {
    if (spinPhase === 'spinning') return;
    roulette.settings.randomEachSpin = !roulette.settings.randomEachSpin;
    notifyChange();
  }

  // 項目数が変化したら idle 時にホイール角度をリセット (古い角度で表示崩れ防止)
  let lastSegCount = $state(-1);
  $effect(() => {
    const n = displayLayout.segments.length;
    if (spinPhase === 'idle' && n !== lastSegCount) {
      activeRt.wheelAngle = 0;
      lastSegCount = n;
    }
  });

  // ─────────────────────────────────────────────────────────
  //  Item 詳細シート用 派生値
  // ─────────────────────────────────────────────────────────

  const detailItem: Item | null = $derived(
    itemDetailIdx !== null && itemDetailIdx < currentSet.items.length
      ? currentSet.items[itemDetailIdx]
      : null,
  );

  const otherFixedSum: number = $derived.by(() => {
    if (itemDetailIdx === null) return 0;
    return currentSet.items.reduce((sum: number, it: Item, i: number) => {
      if (i === itemDetailIdx) return sum;
      if (it.enabled && it.probMode === 'fixed') return sum + (it.probValue ?? 0);
      return sum;
    }, 0);
  });

  const enabledCount: number = $derived(
    currentSet.items.filter((it: Item) => it.enabled).length,
  );

  // 項目別 確率% と 当選回数 (履歴ベース)
  const itemPercentages = $derived(computeItemPercentages(currentSet.items));
  const winCounts = $derived.by(() => {
    const map = new Map<string, number>();
    for (const w of roulette.winHistory) {
      map.set(w.text, (map.get(w.text) ?? 0) + 1);
    }
    return map;
  });
</script>

{#if !ready}
  <div class="loading">読み込み中…</div>
{:else}
  <div class="app-layout">
    <!-- ヘッダー -->
    <header class="header">
      <button
        class="icon-btn"
        aria-label="ルーレット管理"
        onclick={openRouletteManager}
      >≡</button>
      {#if viewMode === 'detail'}
        <button
          class="icon-btn back-btn"
          aria-label="グリッドに戻る"
          title="グリッドに戻る"
          onclick={() => (viewMode = 'grid')}
          disabled={spinPhase === 'spinning' || appState.roulettes.length <= 1}
        >←</button>
        <span class="roulette-name">{roulette.name}</span>
        <button
          class="icon-btn"
          aria-label="設定"
          onclick={openSettings}
          disabled={spinPhase === 'spinning'}
        >⚙</button>
      {:else}
        <span class="roulette-name">ルーレット ({visibleRoulettes.length})</span>
        <button
          class="icon-btn spin-all-btn"
          aria-label="同時実行"
          title="全ルーレット同時実行"
          onclick={() => spinAll()}
        >
          <span class="ic">▶</span>
          <span class="lbl">同時実行</span>
        </button>
      {/if}
    </header>

    {#if viewMode === 'grid'}
      <GridView
        roulettes={visibleRoulettes}
        onenterdetail={(id) => {
          setActiveRoulette(id);
          viewMode = 'detail';
        }}
      />
    {:else}

    <!-- ホイールエリア -->
    <section class="wheel-section">
      <WheelCanvas
        layout={displayLayout}
        rotation={wheelAngle}
        settings={roulette.settings}
        ontap={handleWheelTap}
        onmarkerdrag={handleMarkerDrag}
        glowVariant={activeRt.effectWheelGlow ? activeRt.effectWheelGlowVariant : null}
      />

      <!-- 結果オーバーレイ -->
      {#if winnerVisible && winnerText}
        <div class="result-overlay" role="button" tabindex="0"
             onclick={handleWheelTap} onkeydown={(e) => e.key === 'Enter' && handleWheelTap()}>
          <div class="result-card">
            <p class="result-label">当選</p>
            <p class="result-text">{winnerText}</p>
            <p class="result-hint">タップして閉じる</p>
          </div>
        </div>
      {/if}

      <!-- 特殊演出: フラッシュ (5 variants) -->
      {#key `flash-${activeRt.effectFlashVariant}-${activeRt.effectFlash}`}
        {#if activeRt.effectFlash}
          <div class="effect-flash flash-v{activeRt.effectFlashVariant}" aria-hidden="true"></div>
        {/if}
      {/key}

      <!-- 特殊演出: CHANCE! テキスト (5 variants) -->
      {#key `chance-${activeRt.effectTextChanceVariant}-${activeRt.effectTextChance}`}
        {#if activeRt.effectTextChance}
          <div class="effect-chance chance-v{activeRt.effectTextChanceVariant}" aria-hidden="true">CHANCE!</div>
        {/if}
      {/key}

      <!-- ホイール虹色グローは WheelCanvas 内部で描画 (glowVariant prop 経由) -->

      <!-- 特殊演出: ミニキャラ (ホイール中央で動く) -->
      <MiniCharacterEffect type={activeRt.effectMiniChar} variant={activeRt.effectMiniCharVariant} />

      <!-- スピン状態ヒント -->
      {#if spinPhase === 'idle' && displayLayout.segments.length > 0}
        <div class="spin-hint">
          <span>タップしてスタート</span>
        </div>
      {/if}

      <!-- バージョン表示 (右下隅) -->
      <div class="version-tag">{__APP_VERSION__}</div>

      <!-- 連続実行 進捗 (フローティング) -->
      {#if seqState.phase === 'running'}
        <div class="seq-progress">
          <span class="seq-counter">連続実行 {seqState.current} / {seqState.total}</span>
          {#if seqState.results.length > 0}
            <span class="seq-last">最新: {seqState.results[seqState.results.length - 1].text}</span>
          {/if}
          <button class="seq-stop" onclick={stopSequentialRun}>中断</button>
        </div>
      {/if}
    </section>

    <!-- 操作バー -->
    <div class="op-bar">
      <button class="set-select" onclick={openSetManager} disabled={spinPhase === 'spinning'}>
        <span class="set-name">{currentSet.name}</span>
        <span class="set-arrow">▼</span>
      </button>
      <button
        class="op-btn"
        onclick={openSeqRun}
        disabled={spinPhase === 'spinning' || enabledCount < 2}
      >
        連続実行 ▶
      </button>
    </div>

    <!-- 項目リスト -->
    <section class="item-list" class:collapsed={appState.ui.itemListCollapsed}>
      <div class="item-list-header">
        <span class="item-list-title">
          項目 ({enabledCount}/{currentSet.items.length})
        </span>
        {#if reorderMode}
          <button
            class="header-action"
            onclick={() => (reorderMode = false)}
          >
            完了
          </button>
        {:else}
          <button
            class="header-icon labeled"
            onclick={shuffleNow}
            disabled={spinPhase === 'spinning'}
            title="今すぐシャッフル"
            aria-label="今すぐシャッフル"
          >
            <span class="ic">🔀</span>
            <span class="lbl">並替</span>
          </button>
          {#if isShuffled}
            <button
              class="header-icon labeled"
              onclick={() => restoreOrder()}
              disabled={spinPhase === 'spinning'}
              title="元の順序に戻す"
              aria-label="元の順序に戻す"
            >
              <span class="ic">↩</span>
              <span class="lbl">戻す</span>
            </button>
          {/if}
          <button
            class="header-icon labeled"
            class:active={roulette.settings.randomEachSpin}
            onclick={toggleRandomEachSpin}
            disabled={spinPhase === 'spinning'}
            title="スピンごとにシャッフル"
            aria-label="スピンごとにシャッフル"
            aria-pressed={roulette.settings.randomEachSpin}
          >
            <span class="ic">🔁</span>
            <span class="lbl">毎回</span>
          </button>
          <button
            class="header-icon labeled"
            onclick={() => (reorderMode = true)}
            disabled={spinPhase === 'spinning'}
            title="並び替えモード"
            aria-label="並び替えモード"
          >
            <span class="ic">⇅</span>
            <span class="lbl">移動</span>
          </button>
          <button
            class="header-action"
            onclick={openItemEdit}
            disabled={spinPhase === 'spinning'}
          >
            編集
          </button>
        {/if}
        <button
          class="collapse-btn"
          onclick={() => {
            appState.ui.itemListCollapsed = !appState.ui.itemListCollapsed;
            notifyChange();
          }}
          aria-label={appState.ui.itemListCollapsed ? 'リストを展開' : 'リストを折りたたむ'}
        >
          {appState.ui.itemListCollapsed ? '▲' : '▼'}
        </button>
      </div>

      {#if !appState.ui.itemListCollapsed}
        <ul class="items">
          {#each currentSet.items as item, idx (idx)}
            <li class="item-row" class:disabled={!item.enabled}>
              <span
                class="item-color"
                style:background-color={colorForItem(idx, item)}
                aria-hidden="true"
              ></span>
              {#if reorderMode}
                <button
                  class="reorder-btn"
                  onclick={() => moveItem(currentSet.id, idx, -1)}
                  disabled={idx === 0}
                  aria-label="上へ移動"
                >
                  ▲
                </button>
                <button
                  class="reorder-btn"
                  onclick={() => moveItem(currentSet.id, idx, 1)}
                  disabled={idx === currentSet.items.length - 1}
                  aria-label="下へ移動"
                >
                  ▼
                </button>
                <span class="item-text">{item.text}</span>
              {:else}
                <button
                  class="item-check"
                  role="checkbox"
                  aria-checked={item.enabled}
                  onclick={() => toggleItem(idx)}
                  aria-label={item.enabled ? 'OFF にする' : 'ON にする'}
                >
                  {item.enabled ? '☑' : '☐'}
                </button>
                <button class="item-text-btn" onclick={openItemEdit}>
                  <span class="item-text">{item.text}</span>
                  <span class="item-config">
                    {#if item.probMode === 'fixed'}
                      <span class="badge fixed" title="固定確率設定">固{item.probValue}%</span>
                    {:else if item.probMode === 'weight'}
                      <span class="badge weight" title="重み設定">×{item.probValue}</span>
                    {/if}
                    {#if item.splitCount > 1}
                      <span class="badge split" title="分割数">÷{item.splitCount}</span>
                    {/if}
                  </span>
                  <span class="item-stats">
                    {#if appState.preferences.showItemProbability}
                      {@const pct = item.enabled ? (itemPercentages.get(idx) ?? 0) : 0}
                      <span class="badge prob" class:zero={!item.enabled} title="現在の確率">
                        {item.enabled ? `${pct.toFixed(pct < 10 ? 1 : 0)}%` : '—'}
                      </span>
                    {/if}
                    {#if appState.preferences.showItemWinCount}
                      {@const wc = winCounts.get(item.text) ?? 0}
                      <span class="badge wincount" class:zero={wc === 0} title="当選回数">
                        {wc}回
                      </span>
                    {/if}
                  </span>
                </button>
                {#if item.specialRole === 'target'}
                  <span class="role-badge role-target" title="特殊演出: 当てたい">⭐</span>
                {:else if item.specialRole === 'avoid'}
                  <span class="role-badge role-avoid" title="特殊演出: 当てたくない">❌</span>
                {/if}
                <button
                  class="item-detail-btn"
                  onclick={() => openItemDetail(idx)}
                  aria-label="詳細設定"
                >
                  ⋮
                </button>
              {/if}
            </li>
          {/each}
        </ul>
      {/if}
    </section>
    {/if}
  </div>

  <!-- ─── ボトムシート ─── -->
  <ItemEditSheet
    open={itemEditOpen}
    items={currentSet.items}
    onsave={saveItemEdit}
    onclose={() => (itemEditOpen = false)}
  />

  <ItemDetailSheet
    open={itemDetailIdx !== null}
    item={detailItem}
    {otherFixedSum}
    {enabledCount}
    defaultColor={itemDetailIdx !== null ? colorForItem(itemDetailIdx) : '#888888'}
    onsave={saveItemDetail}
    onclose={() => (itemDetailIdx = null)}
  />

  <SetManagerSheet
    open={setManagerOpen}
    sets={roulette.sets}
    currentSetId={roulette.currentSetId}
    onselect={handleSetSelect}
    onadd={(name) => addSet(name)}
    onrename={(id, name) => renameSet(id, name)}
    ondelete={(id) => deleteSet(id)}
    onclose={() => (setManagerOpen = false)}
  />

  <SettingsSheet
    open={settingsOpen}
    settings={roulette.settings}
    onsave={saveSettings}
    onclose={() => (settingsOpen = false)}
  />

  <AppSettingsSheet
    open={appSettingsOpen}
    preferences={appState.preferences}
    onupdate={(patch) => updatePreferences(patch)}
    onclose={() => (appSettingsOpen = false)}
  />

  <HelpSheet open={helpOpen} onclose={() => (helpOpen = false)} />

  <RouletteManagerSheet
    open={rouletteManagerOpen}
    roulettes={appState.roulettes}
    activeId={appState.activeRouletteId}
    onselect={handleRouletteSelect}
    onadd={(name) => addRoulette(name)}
    onrename={(id, name) => renameRoulette(id, name)}
    ondelete={(id) => deleteRoulette(id)}
    onhelp={() => (helpOpen = true)}
    onhistory={() => (historyOpen = true)}
    onappsettings={() => (appSettingsOpen = true)}
    onvisibleToggle={(id, vis) => setRouletteVisible(id, vis)}
    onduplicate={(id) => duplicateRoulette(id)}
    onclose={() => (rouletteManagerOpen = false)}
  />

  <HistorySheet
    open={historyOpen}
    {roulette}
    onclear={() => clearWinHistory()}
    onclose={() => (historyOpen = false)}
  />

  <SequentialRunSheet
    open={seqRunOpen}
    maxCount={Math.max(1, enabledCount - 1)}
    runState={seqState}
    onstart={(count, holdSec) => startSequentialRun(count, holdSec)}
    onstop={stopSequentialRun}
    onclose={() => {
      if (seqState.phase === 'running') {
        // 実行中は中断要求
        seqStopRequested = true;
      }
      seqRunOpen = false;
      if (seqState.phase === 'done') seqState = { phase: 'idle' };
    }}
  />
<!-- 特殊演出: カットイン (上下黒帯式、全体オーバーレイ) -->
<CharacterEffect type={activeRt.effectCutInChar} variant={activeRt.effectCutInVariant} />

{/if}

<style>
  /* ─── レイアウト ─── */

  .loading {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-fg-muted);
    font-size: var(--font-size-sm);
  }

  .app-layout {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  /* ─── ヘッダー ─── */

  .header {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    padding: var(--space-sm) var(--space-md);
    background: var(--color-surface);
    border-bottom: 1px solid var(--color-border);
    flex-shrink: 0;
    min-height: 48px;
  }

  .roulette-name {
    flex: 1;
    font-size: var(--font-size-base);
    font-weight: 600;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .icon-btn {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-sm);
    font-size: 18px;
    flex-shrink: 0;
    opacity: 0.4;
  }
  .icon-btn:not(:disabled) {
    opacity: 1;
  }
  .icon-btn:not(:disabled):active {
    background: var(--color-border);
  }

  .icon-btn.spin-all-btn {
    width: auto;
    padding: 0 10px;
    height: 36px;
    display: flex;
    flex-direction: row;
    gap: 4px;
    align-items: center;
    color: var(--color-accent);
    font-weight: 700;
    border: 1px solid var(--color-accent);
    border-radius: var(--radius-sm);
  }
  .icon-btn.spin-all-btn .ic {
    font-size: 11px;
  }
  .icon-btn.spin-all-btn .lbl {
    font-size: 12px;
    font-weight: 700;
  }

  .icon-btn.back-btn {
    font-size: 16px;
  }

  /* ─── ホイールエリア ─── */

  .wheel-section {
    flex: 1;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    min-height: 0;
    padding: var(--space-sm);
  }

  /* ─── 結果オーバーレイ ─── */

  .result-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    animation: overlay-in 0.2s ease-out;
    cursor: pointer;
  }

  @keyframes overlay-in {
    from { opacity: 0; transform: scale(0.92); }
    to   { opacity: 1; transform: scale(1); }
  }

  .result-card {
    background: var(--color-surface);
    border-radius: var(--radius-lg);
    padding: var(--space-xl) var(--space-xl);
    max-width: min(320px, 85vw);
    text-align: center;
    box-shadow: var(--shadow-md);
    animation: card-in 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  @keyframes card-in {
    from { transform: scale(0.7); }
    to   { transform: scale(1); }
  }

  .result-label {
    margin: 0 0 var(--space-sm);
    font-size: var(--font-size-sm);
    font-weight: 600;
    color: var(--color-accent);
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .result-text {
    margin: 0 0 var(--space-md);
    font-size: clamp(20px, 6vw, 32px);
    font-weight: 700;
    line-height: 1.25;
    word-break: break-word;
    color: var(--color-fg);
  }

  .result-hint {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-fg-muted);
  }

  /* ─── スピン中ヒント ─── */

  .spin-hint {
    position: absolute;
    bottom: var(--space-sm);
    left: 0;
    right: 0;
    text-align: center;
    pointer-events: none;
  }

  .spin-hint span {
    display: inline-block;
    background: rgba(0, 0, 0, 0.45);
    color: rgba(255, 255, 255, 0.75);
    font-size: 11px;
    padding: 3px 10px;
    border-radius: 100px;
  }

  .version-tag {
    position: absolute;
    bottom: 2px;
    right: 6px;
    font-size: 9px;
    color: var(--color-fg-muted);
    opacity: 0.55;
    font-family: ui-monospace, 'SFMono-Regular', monospace;
    pointer-events: none;
    white-space: nowrap;
  }

  .seq-progress {
    position: absolute;
    top: var(--space-sm);
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    padding: 6px var(--space-md);
    background: rgba(0, 0, 0, 0.75);
    color: #fff;
    border-radius: 100px;
    box-shadow: var(--shadow-md);
    font-size: var(--font-size-sm);
    max-width: 92%;
    white-space: nowrap;
    overflow: hidden;
  }
  .seq-counter {
    font-weight: 700;
    color: var(--color-accent);
    font-variant-numeric: tabular-nums;
  }
  .seq-last {
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }
  .seq-stop {
    background: var(--color-accent);
    color: var(--color-accent-fg);
    padding: 4px 10px;
    border-radius: 100px;
    font-weight: 700;
    font-size: 11px;
    flex-shrink: 0;
  }

  /* ─── 操作バー ─── */

  .op-bar {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    padding: var(--space-sm) var(--space-md);
    background: var(--color-surface);
    border-top: 1px solid var(--color-border);
    flex-shrink: 0;
    min-height: 44px;
  }

  .set-select {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px var(--space-sm);
    border-radius: var(--radius-sm);
    min-width: 0;
    color: var(--color-fg);
  }

  .set-select:active:not(:disabled) {
    background: var(--color-border);
  }

  .set-select:disabled {
    opacity: 0.5;
  }

  .set-name {
    flex: 1;
    font-size: var(--font-size-sm);
    font-weight: 600;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .set-arrow {
    font-size: 10px;
    color: var(--color-fg-muted);
    flex-shrink: 0;
  }

  .op-btn {
    font-size: var(--font-size-sm);
    font-weight: 600;
    padding: 6px var(--space-md);
    border-radius: var(--radius-sm);
    background: var(--color-accent);
    color: var(--color-accent-fg);
    opacity: 0.4;
  }
  .op-btn:not(:disabled) {
    opacity: 1;
  }

  /* ─── 項目リスト ─── */

  .item-list {
    flex-shrink: 0;
    max-height: 40vh;
    display: flex;
    flex-direction: column;
    background: var(--color-bg);
    border-top: 1px solid var(--color-border);
    overflow: hidden;
    transition: max-height 0.2s ease;
  }

  .item-list.collapsed {
    max-height: 44px;
  }

  .item-list-header {
    display: flex;
    align-items: center;
    padding: var(--space-sm) var(--space-md);
    min-height: 44px;
    flex-shrink: 0;
    border-bottom: 1px solid var(--color-border);
  }

  .item-list-title {
    flex: 1;
    font-size: var(--font-size-sm);
    font-weight: 600;
    color: var(--color-fg-muted);
  }

  .header-action {
    font-size: var(--font-size-sm);
    font-weight: 600;
    color: var(--color-accent);
    padding: 6px 10px;
    border-radius: var(--radius-sm);
  }

  .header-action:active:not(:disabled) {
    background: rgba(233, 69, 96, 0.1);
  }

  .header-action:disabled {
    opacity: 0.4;
  }

  .header-icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    border-radius: var(--radius-sm);
    color: var(--color-fg-muted);
    line-height: 1;
  }

  .header-icon.labeled {
    width: auto;
    height: 32px;
    padding: 0 8px;
    flex-direction: row;
    gap: 4px;
  }

  .header-icon.labeled .ic {
    font-size: 14px;
    line-height: 1;
  }

  .header-icon.labeled .lbl {
    font-size: 11px;
    line-height: 1;
    color: var(--color-fg-muted);
    font-weight: 500;
  }

  .header-icon.labeled.active .lbl {
    color: var(--color-accent);
    font-weight: 700;
  }

  .header-icon:active:not(:disabled) {
    background: var(--color-border);
  }

  .header-icon.active {
    background: rgba(233, 69, 96, 0.18);
    color: var(--color-accent);
  }

  .header-icon:disabled {
    opacity: 0.35;
  }

  .collapse-btn {
    font-size: 12px;
    color: var(--color-fg-muted);
    padding: 4px 8px;
    margin-left: 4px;
  }

  .items {
    list-style: none;
    margin: 0;
    padding: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }

  .item-row {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px var(--space-sm);
    border-bottom: 1px solid var(--color-border);
    min-height: 48px;
  }

  .item-color {
    width: 4px;
    height: 28px;
    border-radius: 2px;
    flex-shrink: 0;
    margin-right: 4px;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06);
  }

  .item-row.disabled .item-color {
    opacity: 0.3;
  }

  .reorder-btn {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: var(--color-fg-muted);
    border-radius: var(--radius-sm);
    flex-shrink: 0;
  }

  .reorder-btn:active:not(:disabled) {
    background: var(--color-border);
    color: var(--color-accent);
  }

  .reorder-btn:disabled {
    opacity: 0.25;
  }

  .item-row.disabled .item-text {
    color: var(--color-fg-muted);
    text-decoration: line-through;
  }

  .item-check {
    width: 36px;
    height: 36px;
    font-size: 20px;
    line-height: 1;
    flex-shrink: 0;
    color: var(--color-accent);
    border-radius: var(--radius-sm);
  }

  .item-check:active {
    background: var(--color-border);
  }

  .item-text-btn {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 8px var(--space-sm);
    text-align: left;
    border-radius: var(--radius-sm);
    min-width: 0;
    color: var(--color-fg);
  }

  .item-text-btn:active {
    background: var(--color-border);
  }

  .item-text {
    flex: 1;
    font-size: var(--font-size-base);
    word-break: break-word;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .item-config {
    display: flex;
    gap: 3px;
    flex-shrink: 0;
  }

  /* 右端の確率/当選回数 - min-width で全行縦に揃う */
  .item-stats {
    display: flex;
    gap: 3px;
    flex-shrink: 0;
  }

  .item-stats .badge {
    min-width: 42px;
    text-align: center;
    justify-content: center;
    display: inline-flex;
    align-items: center;
  }

  .badge {
    flex-shrink: 0;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 100px;
    line-height: 1.4;
  }

  .badge.fixed {
    background: rgba(233, 69, 96, 0.18);
    color: var(--color-accent);
  }

  .badge.weight {
    background: rgba(58, 134, 255, 0.18);
    color: #3a86ff;
  }

  .badge.split {
    background: rgba(42, 157, 143, 0.18);
    color: #2a9d8f;
  }

  .badge.prob {
    background: var(--color-border);
    color: var(--color-fg);
    font-variant-numeric: tabular-nums;
  }

  .badge.wincount {
    background: rgba(255, 193, 7, 0.22);
    color: #cc8800;
    font-variant-numeric: tabular-nums;
  }

  .badge.prob.zero,
  .badge.wincount.zero {
    opacity: 0.4;
    background: var(--color-border);
    color: var(--color-fg-muted);
  }

  .item-detail-btn {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: var(--color-fg-muted);
    border-radius: var(--radius-sm);
    flex-shrink: 0;
  }

  .item-detail-btn:active {
    background: var(--color-border);
  }

  /* 項目行の specialRole バッジ (当選回数と⋮の間) */
  .role-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    font-size: 14px;
    line-height: 1;
    border-radius: var(--radius-sm);
    flex-shrink: 0;
    user-select: none;
  }

  .role-badge.role-target {
    background: rgba(255, 200, 0, 0.18);
  }

  .role-badge.role-avoid {
    background: rgba(255, 80, 80, 0.18);
  }

  /* ─── 特殊演出オーバーレイ ─── */

  /* ──── フラッシュ 5 バリエーション ──── */
  .effect-flash {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 150;
    border-radius: inherit;
  }

  /* V1 ゴールド ラジアル (現状) */
  @keyframes flash-gold {
    0%   { opacity: 0; }
    8%   { opacity: 0.85; }
    100% { opacity: 0; }
  }
  .flash-v1 {
    background: radial-gradient(circle, rgba(255,215,0,0.92) 0%, rgba(255,120,0,0.55) 100%);
    animation: flash-gold 0.9s ease-out forwards;
  }

  /* V2 純白フラッシュ (短く強烈、瞬間) */
  @keyframes flash-white {
    0%   { opacity: 0; }
    5%   { opacity: 1; }
    20%  { opacity: 0.5; }
    100% { opacity: 0; }
  }
  .flash-v2 {
    background: rgba(255,255,255,0.95);
    animation: flash-white 0.5s ease-out forwards;
  }

  /* V3 赤フラッシュ (緊急感) */
  @keyframes flash-red {
    0%   { opacity: 0; }
    10%  { opacity: 0.85; }
    100% { opacity: 0; }
  }
  .flash-v3 {
    background: radial-gradient(circle, rgba(255,30,30,0.95) 0%, rgba(180,0,0,0.6) 100%);
    animation: flash-red 0.9s ease-out forwards;
  }

  /* V4 虹色フラッシュ (色相回転) */
  @keyframes flash-rainbow {
    0%   { opacity: 0; filter: hue-rotate(0deg); }
    10%  { opacity: 0.85; filter: hue-rotate(0deg); }
    100% { opacity: 0; filter: hue-rotate(360deg); }
  }
  .flash-v4 {
    background: radial-gradient(circle, rgba(255,80,80,0.85) 0%, rgba(255,255,80,0.4) 100%);
    animation: flash-rainbow 1.0s ease-out forwards;
  }

  /* V5 パルス (2回点滅) */
  @keyframes flash-pulse {
    0%, 30%, 60%, 100% { opacity: 0; }
    10%, 40%           { opacity: 0.85; }
  }
  .flash-v5 {
    background: radial-gradient(circle, rgba(255,200,0,0.92) 0%, rgba(255,80,0,0.5) 100%);
    animation: flash-pulse 1.0s linear forwards;
  }

  /* ──── CHANCE! テキスト 5 バリエーション ──── */
  .effect-chance {
    position: absolute;
    top: 50%;
    left: 50%;
    font-size: clamp(36px, 10vw, 56px);
    font-weight: 900;
    color: #ffd700;
    text-shadow: 0 0 18px #ff6600, 2px 2px 0 #000, -1px -1px 0 #000;
    letter-spacing: 0.06em;
    pointer-events: none;
    z-index: 160;
    white-space: nowrap;
    will-change: transform, opacity;
  }

  /* V1 ポップアップ (現状) */
  @keyframes chance-pop {
    0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.4); }
    18%  { opacity: 1; transform: translate(-50%, -50%) scale(1.15); }
    30%  { transform: translate(-50%, -50%) scale(1); }
    72%  { opacity: 1; }
    100% { opacity: 0; transform: translate(-50%, -50%) scale(0.85) translateY(-24px); }
  }
  .chance-v1 { animation: chance-pop 1.6s ease-out forwards; }

  /* V2 上から落下 */
  @keyframes chance-drop {
    0%   { opacity: 0; transform: translate(-50%, -200%) scale(1.3); }
    25%  { opacity: 1; transform: translate(-50%, -42%) scale(0.9); }
    35%  { transform: translate(-50%, -50%) scale(1.1); }
    45%  { transform: translate(-50%, -50%) scale(1); }
    80%  { opacity: 1; transform: translate(-50%, -50%) scale(1); }
    100% { opacity: 0; transform: translate(-50%, -50%) scale(1.6); }
  }
  .chance-v2 { animation: chance-drop 1.6s cubic-bezier(0.4, 0, 0.4, 1) forwards; }

  /* V3 振動シェイク */
  @keyframes chance-shake {
    0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.4); }
    15%  { opacity: 1; transform: translate(-50%, -50%) scale(1.2); }
    18%  { transform: translate(-52%, -50%) scale(1); }
    21%  { transform: translate(-48%, -50%) scale(1); }
    24%  { transform: translate(-52%, -50%) scale(1); }
    27%  { transform: translate(-48%, -50%) scale(1); }
    30%  { transform: translate(-50%, -50%) scale(1); }
    80%  { opacity: 1; transform: translate(-50%, -50%) scale(1); }
    100% { opacity: 0; transform: translate(-50%, -50%) scale(0.7); }
  }
  .chance-v3 { animation: chance-shake 1.6s ease-out forwards; }

  /* V4 拡大グロー */
  @keyframes chance-glow {
    0%   { opacity: 0; transform: translate(-50%, -50%) scale(0); text-shadow: 0 0 0 transparent; }
    25%  { opacity: 1; transform: translate(-50%, -50%) scale(1.3);
           text-shadow: 0 0 30px #ffaa00, 0 0 60px #ff6600, 2px 2px 0 #000; }
    40%  { transform: translate(-50%, -50%) scale(1); }
    75%  { opacity: 1; transform: translate(-50%, -50%) scale(1.1);
           text-shadow: 0 0 40px #ffd700, 0 0 80px #ff8800, 2px 2px 0 #000; }
    100% { opacity: 0; transform: translate(-50%, -50%) scale(1.4); }
  }
  .chance-v4 { animation: chance-glow 1.7s ease-in-out forwards; }

  /* V5 横スライドイン (左から飛び込み) */
  @keyframes chance-slide {
    0%   { opacity: 0; transform: translate(-300%, -50%) scale(0.7) skewX(-30deg); }
    25%  { opacity: 1; transform: translate(-50%, -50%) scale(1.15) skewX(0); }
    35%  { transform: translate(-50%, -50%) scale(1) skewX(0); }
    75%  { opacity: 1; transform: translate(-50%, -50%) scale(1) skewX(0); }
    100% { opacity: 0; transform: translate(200%, -50%) scale(0.7) skewX(30deg); }
  }
  .chance-v5 { animation: chance-slide 1.6s cubic-bezier(0.3, 0.7, 0.4, 1) forwards; }

  /* ホイール虹色グローは WheelCanvas 内で Canvas 描画されるため CSS なし */
</style>
